import { Artist } from "@prisma/client";
import { useMatches } from "@remix-run/react";
import { useMemo } from "react";

import type { User } from "~/models/user.server";

const DEFAULT_REDIRECT = "/";

/**
 * This should be used any time the redirect path is user-provided
 * (Like the query string on our login/signup pages). This avoids
 * open-redirect vulnerabilities.
 * @param {string} to The redirect destination
 * @param {string} defaultRedirect The redirect to use if the to is unsafe.
 */
export function safeRedirect(
  to: FormDataEntryValue | string | null | undefined,
  defaultRedirect: string = DEFAULT_REDIRECT,
) {
  if (!to || typeof to !== "string") {
    return defaultRedirect;
  }

  if (!to.startsWith("/") || to.startsWith("//")) {
    return defaultRedirect;
  }

  return to;
}

/**
 * This base hook is used in other hooks to quickly search for specific data
 * across all loader data using useMatches.
 * @param {string} id The route id
 * @returns {JSON|undefined} The router data or undefined if not found
 */
export function useMatchesData(
  id: string,
): Record<string, unknown> | undefined {
  const matchingRoutes = useMatches();
  const route = useMemo(
    () => matchingRoutes.find((route) => route.id === id),
    [matchingRoutes, id],
  );
  return route?.data as Record<string, unknown>;
}

function isUser(user: unknown): user is User {
  return (
    user != null &&
    typeof user === "object" &&
    "username" in user &&
    typeof user.username === "string"
  );
}

export function useOptionalUser(): User | undefined {
  const data = useMatchesData("routes/base-layout");
  if (!data || !isUser(data.user)) {
    return undefined;
  }
  return data.user;
}

export function useUser(): User {
  const maybeUser = useOptionalUser();
  if (!maybeUser) {
    throw new Error(
      "No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.",
    );
  }
  return maybeUser;
}

export function useOptionalArtists(): Artist[] | undefined {
  const data = useMatchesData("routes/base-layout");
  if (!data || !Array.isArray(data.artists)) {
    return undefined;
  }
  return data.artists as Artist[];
}

export function useArtists(): Artist[] {
  const maybeArtists = useOptionalArtists();
  if (!maybeArtists) {
    throw new Error(
      "No artists found in root loader, but artists are required by useArtists. If artists are optional, try useOptionalArtists instead.",
    );
  }
  return maybeArtists;
}

export function useOptionalMatchCount(): number | undefined {
  const data = useMatchesData("routes/base-layout");
  if (!data || typeof data.matchCount !== "number") {
    return undefined;
  }
  return data.matchCount as number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface DebounceFunction<T extends (...args: any[]) => any> {
  (this: ThisParameterType<T>, ...args: Parameters<T>): void;
  cancel(): void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
): DebounceFunction<T> {
  let timeout: ReturnType<typeof setTimeout>;

  const debouncedFunction = function (
    this: ThisParameterType<T>,
    ...args: Parameters<T>
  ) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };

  debouncedFunction.cancel = () => {
    clearTimeout(timeout);
  };

  return debouncedFunction;
}

export function buildMetaTitle(routeName?: string): string {
  if (!routeName) {
    return "poke";
  }

  if (routeName === "poke") {
    return "poke";
  }

  return `${routeName} | poke`;
}

type MetaTag = { property?: string; content: string } | { title: string };

export function buildMetaTags(
  params: {
    routeName?: string;
    description?: string;
    imageUrl?: string;
    url?: string;
  } = {},
): MetaTag[] {
  const {
    routeName,
    description = "pick your music, find your matches",
    url = "https://poke.osc.wtf",
    imageUrl = "https://poke.osc.wtf/assets/og-image.png",
  } = params;

  const title = buildMetaTitle(routeName);
  return [
    { title },
    {
      property: "description",
      content: description,
    },
    { property: "twitter:card", content: "summary_large_image" },
    { property: "twitter:domain", content: "poke.osc.wtf" },
    { property: "twitter:site", content: "@OscillatorInc" },
    { property: "twitter:image", content: imageUrl },
    { property: "twitter:title", content: title },
    { property: "twitter:description", content: description },
    { property: "og:url", content: url },
    { property: "og:title", content: title },
    { property: "og:type", content: "website" },
    { property: "og:description", content: description },
    {
      property: "og:image",
      content: imageUrl,
    },
  ];
}

export function buildOgProfileUrl({
  apiUrl,
  userId,
}: {
  apiUrl: string;
  userId: string;
}): string {
  const ogUrlParams = new URLSearchParams();
  ogUrlParams.set("userId", userId);
  return `${apiUrl}/api/og/profile?${ogUrlParams.toString()}`;
}
